/**
 * `@joshua/supabase-guard` — the fetch wrapper passed to supabase-js as
 * `global.fetch` (JOSH-89).
 *
 * It observes; it never rewrites auth:
 * - `/auth/v1/*` always passes through untouched (those calls run inside the
 *   auth lock, so the guard never refreshes from them);
 * - a data, file or function request rejected for an expired token triggers
 *   one single-flight `refreshSession()` and, for the same user and auth
 *   session only, one replay with the new Bearer;
 * - during a refresh cooldown, that rejection becomes a local 401
 *   `SESSION_RECONNECTING`. It stays 401 because postgrest-js retries a GET
 *   that gets 503/520, which would turn one read into four;
 * - health latches `reconnecting` when the bound client reports a refresh
 *   cooldown, and goes back to `ok` only on a real refresh, sign-in, sign-out
 *   or unbind (see health.ts).
 *
 * All state lives in the instance closure. Bodies and headers are never logged.
 */

import { EXPIRY_STATUSES, isTokenExpired, readErrorBody } from './expired';
import { createHealthStore, type HealthStore, type RefreshCooldown } from './health';
import { bearerToken, sameIdentity } from './jwtIdentity';
import { createRouteClassifier } from './routes';
import { createSingleFlight, withTimeout } from './singleFlight';

/** Body code of the local answer given while the session is reconnecting. */
export const SESSION_RECONNECTING_CODE = 'SESSION_RECONNECTING';

/** Body message of the local answer given while the session is reconnecting. */
export const SESSION_RECONNECTING_MESSAGE = 'Session is reconnecting; retry shortly.';

/**
 * Header that marks the local reconnecting answer. It never reaches the
 * network; it lets callers that only keep the Response (functions-js puts it
 * in `FunctionsHttpError.context`) recognize the answer without its body.
 */
export const SESSION_RECONNECTING_HEADER = 'x-joshua-session';
const SESSION_RECONNECTING_HEADER_VALUE = 'reconnecting';

/** Whether a Response is the guard's local reconnecting answer. */
export function isReconnectingResponse(response: unknown): boolean {
  // Duck-typed: a fetch polyfill may supply a different Response class.
  const headers = (response as { headers?: { get?: unknown } } | null | undefined)?.headers;
  if (!headers || typeof headers.get !== 'function') return false;
  return (headers as Headers).get(SESSION_RECONNECTING_HEADER) === SESSION_RECONNECTING_HEADER_VALUE;
}

/** Whether an error's `context` is the guard's reconnecting Response (functions-js). */
export function hasReconnectingContext(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  return isReconnectingResponse((error as { context?: unknown }).context);
}

/**
 * Whether an error carries the reconnecting answer: as `code` (postgrest-js)
 * or `statusCode` (storage-js), on itself or its `cause`, or as a
 * reconnecting Response in `context` (functions-js).
 */
export function hasReconnectingCode(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const carriesCode = (value: unknown): boolean => {
    if (!value || typeof value !== 'object') return false;
    const e = value as { code?: unknown; statusCode?: unknown };
    return e.code === SESSION_RECONNECTING_CODE || e.statusCode === SESSION_RECONNECTING_CODE;
  };
  return carriesCode(error) || carriesCode((error as { cause?: unknown }).cause) || hasReconnectingContext(error);
}
const DEFAULT_REFRESH_TIMEOUT_MS = 15_000;
/** Auth events that end a reconnecting stretch: a session was saved or removed. */
const RELEASE_EVENTS: ReadonlySet<string> = new Set(['SIGNED_IN', 'SIGNED_OUT', 'TOKEN_REFRESHED']);

type FetchFn = typeof fetch;

/**
 * The slice of a supabase-js client the guard uses, typed structurally so this
 * package never imports `@supabase/*`. `getRefreshCooldown` comes from the
 * auth-js 2.104.0 patch.
 */
export interface GuardAuthClient {
  auth: {
    refreshSession(): Promise<{
      data: { session: { access_token: string } | null };
      error: unknown;
    }>;
    getRefreshCooldown(): RefreshCooldown | null;
    onAuthStateChange(callback: (event: string, session: { access_token?: string } | null) => void): {
      data: { subscription: { unsubscribe(): void } };
    };
  };
}

export interface GuardBindOptions {
  /** Gate for guard-triggered refreshes (Ops: inactivity timeout and seal). Default: always. */
  mayRefresh?: () => boolean;
  /** How long a rejected request waits for the refresh. Default 15 s. */
  refreshTimeoutMs?: number;
  /**
   * The bound session's current access token. Default: the token from the
   * latest auth event the guard saw.
   */
  currentAccessToken?: () => string | null | undefined;
}

export interface SessionGuardOptions {
  supabaseUrl: string;
  /** Defaults to `globalThis.fetch`, read at call time. */
  baseFetch?: FetchFn;
}

export interface SessionGuard {
  fetch: FetchFn;
  /** Binds the client created with `fetch`; returns an unbind function. */
  bind(client: GuardAuthClient, options?: GuardBindOptions): () => void;
  health: HealthStore;
}

type RefreshOutcome = { kind: 'refreshed'; accessToken: string } | { kind: 'cooldown' } | { kind: 'failed' };

interface Binding {
  client: GuardAuthClient;
  mayRefresh: () => boolean;
  refreshTimeoutMs: number;
  currentAccessToken: () => string | null | undefined;
  refresh: () => Promise<RefreshOutcome>;
}

const FAILED: RefreshOutcome = { kind: 'failed' };

/** The patched auth-js answer for a rate-limited refresh. */
function isCooldownError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: unknown; status?: unknown };
  return e.name === 'AuthRetryableFetchError' && e.status === 429;
}

function reconnectingResponse(): Response {
  return new Response(
    JSON.stringify({ code: SESSION_RECONNECTING_CODE, message: SESSION_RECONNECTING_MESSAGE }),
    {
      status: 401,
      headers: {
        'Content-Type': 'application/json',
        [SESSION_RECONNECTING_HEADER]: SESSION_RECONNECTING_HEADER_VALUE,
      },
    },
  );
}

function asRequest(input: RequestInfo | URL): Request | null {
  return typeof Request !== 'undefined' && input instanceof Request ? input : null;
}

function isReplayableBody(input: RequestInfo | URL, init: RequestInit | undefined): boolean {
  if (init?.body != null) {
    return !(typeof ReadableStream !== 'undefined' && init.body instanceof ReadableStream);
  }
  // A Request's own body is a one-shot stream.
  return !asRequest(input)?.body;
}

/** Creates one guard instance; nothing is shared between instances. */
export function createSessionGuard({ supabaseUrl, baseFetch }: SessionGuardOptions): SessionGuard {
  const classify = createRouteClassifier(supabaseUrl);
  const replays = new WeakSet<RequestInit>();
  let binding: Binding | null = null;

  const health = createHealthStore(() => binding?.client.auth.getRefreshCooldown() ?? null);
  const send: FetchFn = (input, init) => (baseFetch ?? globalThis.fetch)(input, init);

  /** Recomputes once auth-js has processed a refresh-grant response. */
  const observeRefreshGrant = (response: Response): void => {
    health.observe();
    // auth-js sets the cooldown only after reading this body; one macrotask
    // after the body is read, its microtask chain has finished.
    let bodyRead: Promise<unknown>;
    try {
      bodyRead = response.clone().text();
    } catch {
      bodyRead = Promise.resolve();
    }
    void bodyRead.catch(() => undefined).then(() => setTimeout(health.observe, 0));
  };

  const refreshAndReplay = async (
    current: Binding,
    response: Response,
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    sentToken: string | null,
  ): Promise<Response> => {
    const outcome = await withTimeout(current.refresh(), current.refreshTimeoutMs, FAILED);
    if (outcome.kind === 'cooldown') return reconnectingResponse();
    if (outcome.kind !== 'refreshed') return response;

    const headers = new Headers(init?.headers ?? asRequest(input)?.headers);
    const original = bearerToken(headers.get('Authorization'));
    const signal = init?.signal ?? asRequest(input)?.signal;
    const replayable =
      original !== null &&
      original === sentToken &&
      sameIdentity(original, outcome.accessToken) &&
      !signal?.aborted &&
      isReplayableBody(input, init);
    if (!replayable) return response;

    headers.set('Authorization', `Bearer ${outcome.accessToken}`);
    const replayInit: RequestInit = { ...init, headers };
    replays.add(replayInit);
    return send(input, replayInit);
  };

  const guardedFetch: FetchFn = async (input, init) => {
    if (init && replays.has(init)) return send(input, init);
    const route = classify(input);
    if (route.kind === 'other') return send(input, init);

    if (route.kind === 'auth') {
      const response = await send(input, init);
      if (route.refreshGrant) observeRefreshGrant(response);
      return response;
    }

    const current = binding;
    const sentToken = current?.currentAccessToken() ?? null;
    const response = await send(input, init);
    health.observe();
    if (!current || !EXPIRY_STATUSES[route.kind].has(response.status)) return response;
    if (!isTokenExpired(route.kind, response.status, await readErrorBody(response))) return response;
    if (!current.mayRefresh()) return response;
    return refreshAndReplay(current, response, input, init, sentToken);
  };

  const bind = (client: GuardAuthClient, options: GuardBindOptions = {}): (() => void) => {
    let latestToken: string | null = null;
    const refresh = createSingleFlight<RefreshOutcome>(async () => {
      try {
        const { data, error } = await client.auth.refreshSession();
        if (isCooldownError(error)) return { kind: 'cooldown' };
        const accessToken = data?.session?.access_token;
        if (!error && accessToken) {
          health.release();
          return { kind: 'refreshed', accessToken };
        }
        return FAILED;
      } catch {
        return FAILED;
      } finally {
        health.observe();
      }
    });
    const next: Binding = {
      client,
      mayRefresh: options.mayRefresh ?? (() => true),
      refreshTimeoutMs: options.refreshTimeoutMs ?? DEFAULT_REFRESH_TIMEOUT_MS,
      currentAccessToken: options.currentAccessToken ?? (() => latestToken),
      refresh,
    };
    binding = next;

    // Synchronous on purpose: auth-js may call it while holding the auth lock.
    const { data } = client.auth.onAuthStateChange((event, session) => {
      latestToken = session?.access_token ?? null;
      if (RELEASE_EVENTS.has(event)) health.release();
    });
    health.observe();

    return () => {
      data.subscription.unsubscribe();
      if (binding === next) {
        binding = null;
        health.release();
      }
    };
  };

  return { fetch: guardedFetch, bind, health: health.store };
}
